import { formatIDR } from "@kanata/shared";

import type { ApiSku } from "@/lib/api";
import { savings } from "@/lib/pdp-pricing";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

/**
 * Price, savings, and — once add-ons are ticked — the running total.
 *
 * The compare-at strike-through is not limited to packages: the server sends
 * compareAtIdr wherever it has one, and the page should say "Hemat" wherever it
 * is true.
 */
export function PriceBlock({
  selected,
  addonsTotal,
  total,
  isInquiry,
  className,
}: {
  selected: ApiSku | undefined;
  addonsTotal: number;
  total: number | undefined;
  /** A made-to-order choice is selected, so there is no price to quote. */
  isInquiry?: boolean;
  className?: string;
}) {
  // Checked before `selected`, because an inquiry selection also has no SKU —
  // and "Pilih varian" would read as though the shopper had not finished
  // choosing when in fact they have chosen something that is quoted by hand.
  if (isInquiry) {
    return (
      <p className={cn("text-2xl font-medium text-ink/70", className)}>Harga sesuai konsultasi</p>
    );
  }
  if (!selected) {
    return <p className={cn("text-2xl font-medium text-ink/70", className)}>Pilih varian</p>;
  }
  const save = savings(selected);

  return (
    <div className={className}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-2xl font-medium">{formatIDR(selected.priceIdr)}</span>
        {save && selected.compareAtIdr && (
          <del className="text-ink/45">{formatIDR(selected.compareAtIdr)}</del>
        )}
        {save && (
          // Rupiah only, as on Paket Hemat: the client finds "Hemat 4%" on a
          // desk undersells a saving of Rp 150.000.
          <Badge variant="sale">Hemat {formatIDR(save.amount)}</Badge>
        )}
      </div>

      {addonsTotal > 0 && total !== undefined && (
        <dl className="mt-3 space-y-1.5 rounded-xl bg-cream px-4 py-3 text-sm">
          <Row label="Produk" value={formatIDR(selected.priceIdr)} />
          <Row label="Aksesori" value={`+ ${formatIDR(addonsTotal)}`} />
          <Separator className="my-1" />
          <Row label="Total" value={formatIDR(total)} strong />
        </dl>
      )}

      {selected.priceInvalid && (
        <p role="status" className="mt-2 text-sm text-ink/65">
          Paket sementara tidak tersedia. Harga sedang diperbarui.
        </p>
      )}
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink/60">{label}</dt>
      <dd className={cn("tabular-nums", strong && "font-medium")}>{value}</dd>
    </div>
  );
}
